/**
 * PageShell — standard Configuration-module page wrapper.
 *
 * Matches the Invitee / Properties layout: full-width header bar from
 * `PageHeader` + a padded content column below. Use for every config
 * sidebar child so the header is consistent.
 */

import type { ReactNode } from 'react'
import { cn } from '@lib/utils/helpers'
import { PageHeader } from '@/features/invitee/components/PageHeader'

interface PageShellProps {
  title: string
  children: ReactNode
  /** Optional extra action(s) rendered in the content area (above children). */
  headerAction?: ReactNode
  /** Merges with default content wrapper classes (padding, max-width, vertical rhythm). */
  contentClassName?: string
  /** Optional `data-testid` root for the blue title strip (default Invitee list id). */
  pageHeaderTestIdRoot?: string
  /** Shows the dashboard breadcrumb row below the page header. */
  showBreadcrumbs?: boolean
}

export function PageShell({
  title,
  children,
  headerAction,
  contentClassName,
  pageHeaderTestIdRoot,
  showBreadcrumbs = true,
}: PageShellProps) {
  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12">
      <PageHeader
        title={title}
        testIdRoot={pageHeaderTestIdRoot}
        showBreadcrumbs={showBreadcrumbs}
      />
      <div
        className={cn(
          'w-full px-3 sm:px-6 py-4 sm:py-6 space-y-4',
          contentClassName,
        )}
      >
        {headerAction && (
          <div className="flex w-full min-w-0 items-center justify-end">
            {headerAction}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}
